'use client';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, XAxis, YAxis } from 'recharts';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { ChartContainer as ShadcnChartContainer, ChartTooltip } from '@/components/ui/chart';
import { Button } from '@/components/ui/button';
import { formatMoney } from "@/lib/money";
import { EmptyState } from './EmptyState';

type GeographicDistributionData = {
    countryCode: string;
    countryName: string;
    transactionCount: number;
    amount: number;
};

export type CustomerGeographicViewMode = 'cardCountry' | 'ipCountry';

interface CustomerGeographicChartProps {
    cardCountryData: GeographicDistributionData[] | undefined;
    ipCountryData: GeographicDistributionData[] | undefined;
    currency: string;
    className?: string;
    viewMode?: CustomerGeographicViewMode;
}

const DEFAULT_VISIBLE_COUNT = 7;

const CHART_COLORS = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
];

function getCountryColor(index: number): string {
    return CHART_COLORS[index % CHART_COLORS.length];
}

// Country flag emoji from country code
function getCountryFlag(countryCode: string): string {
    try {
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    } catch {
        return '';
    }
}

export function CustomerGeographicChart({
    cardCountryData,
    ipCountryData,
    currency,
    className = '',
    viewMode = 'cardCountry'
}: CustomerGeographicChartProps) {
    const { t } = useTranslation('analytics');
    const [isExpanded, setIsExpanded] = useState(false);

    const data = viewMode === 'cardCountry' ? cardCountryData : ipCountryData;

    const allChartData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.map(item => ({
            name: `${getCountryFlag(item.countryCode)} ${item.countryName}`,
            code: item.countryCode,
            value: item.amount,
            count: item.transactionCount
        }));
    }, [data]);

    const hasMore = allChartData.length > DEFAULT_VISIBLE_COUNT;
    const chartData = isExpanded ? allChartData : allChartData.slice(0, DEFAULT_VISIBLE_COUNT);
    const hiddenCount = allChartData.length - DEFAULT_VISIBLE_COUNT;

    const formatAxisValue = (value: number) => {
        const majorUnits = value / 100;
        if (majorUnits >= 1000000) return `${(majorUnits / 1000000).toFixed(1)}M`;
        if (majorUnits >= 1000) return `${(majorUnits / 1000).toFixed(0)}K`;
        return `${majorUnits.toFixed(0)}`;
    };

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const item = payload[0].payload;
        const label = viewMode === 'cardCountry' ? t('revenue.transactions') : t('charts.orders');
        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{item.name}</p>
                <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.revenue')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{formatMoney(item.value, currency)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{label}:</span>
                        <span className="text-sm font-medium text-card-foreground">{item.count.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        );
    };

    if (!allChartData.length) {
        return <EmptyState message={t('charts.noGeoData')} className={className} />;
    }

    const chartHeight = Math.max(180, chartData.length * 36);

    const config: Record<string, { label: string; color: string }> = {};
    chartData.forEach((item, index) => {
        config[item.code] = { label: item.name, color: getCountryColor(index) };
    });

    return (
        <div className={`w-full ${className}`}>
            <div style={{ height: chartHeight }}>
                <ShadcnChartContainer config={config} className="w-full h-full">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                        <XAxis type="number" tickFormatter={formatAxisValue} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={140}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <ChartTooltip cursor={false} content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {chartData.map((entry, index) => (
                                <Cell key={entry.code} fill={getCountryColor(index)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ShadcnChartContainer>
            </div>
            {hasMore && (
                <div className="mt-2 flex justify-center">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUp className="mr-1 h-4 w-4" />
                                {t('charts.showLess')}
                            </>
                        ) : (
                            <>
                                <ChevronDown className="mr-1 h-4 w-4" />
                                {t('charts.showMore', { count: hiddenCount })}
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}
